/**
 * TypewriterShowcase Component
 *
 * A compact typewriter animation that cycles through market update examples.
 * Designed for use in the paywall feature showcase.
 * No blur effect - just typing and fading between bullets.
 */

import { FC, useState, useEffect, useRef } from 'react';
import { css, keyframes } from '@emotion/react';
import { colors, spacing, fontStyles } from '../../styles';

// Keyframes for fade transition
const fadeInKeyframes = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOutKeyframes = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

// Cursor blink animation
const blinkKeyframes = keyframes`
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
`;

export interface TypewriterBullet {
  emoji: string;
  text: string;
}

interface TypewriterShowcaseProps {
  bullets?: TypewriterBullet[];
  typingDuration?: number; // ms to type each bullet
  holdDuration?: number; // ms to hold after typing
}

const DEFAULT_BULLETS: TypewriterBullet[] = [
  { emoji: '🟢', text: 'NVDA +8% on strong AI chip demand' },
  { emoji: '🔴', text: 'Fed holds rates, markets dip 0.5%' },
  { emoji: '⚪', text: 'AAPL announces Q2 earnings date' },
  { emoji: '🟢', text: 'Your portfolio up 2.3% today' },
];

export const TypewriterShowcase: FC<TypewriterShowcaseProps> = ({
  bullets = DEFAULT_BULLETS,
  typingDuration = 1200,
  holdDuration = 1500,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState<'typing' | 'holding' | 'fading'>('typing');
  const [displayText, setDisplayText] = useState('');
  const animationRef = useRef<number | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const currentBullet = bullets[currentIndex];

  // Character-by-character typing effect
  useEffect(() => {
    if (phase !== 'typing') return;

    const text = currentBullet.text;
    const charDelay = typingDuration / text.length;
    let charIndex = 0;

    setDisplayText('');

    const typeNextChar = () => {
      if (charIndex < text.length) {
        setDisplayText(text.slice(0, charIndex + 1));
        charIndex++;
        animationRef.current = requestAnimationFrame(() => {
          timeoutRef.current = setTimeout(typeNextChar, charDelay);
        });
      } else {
        // Typing complete, move to holding phase
        setPhase('holding');
      }
    };

    typeNextChar();

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [phase, currentIndex, currentBullet.text, typingDuration]);

  // Hold phase - wait then fade
  useEffect(() => {
    if (phase !== 'holding') return;

    const timeout = setTimeout(() => {
      setPhase('fading');
    }, holdDuration);

    return () => clearTimeout(timeout);
  }, [phase, holdDuration]);

  // Fading phase - transition to next bullet
  useEffect(() => {
    if (phase !== 'fading') return;

    const timeout = setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % bullets.length);
      setPhase('typing');
    }, 300); // Fade duration

    return () => clearTimeout(timeout);
  }, [phase, bullets.length]);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${spacing.milli};
      `}
    >
      {/* Current animated bullet */}
      <div
        css={css`
          display: flex;
          align-items: flex-start;
          gap: ${spacing.milli};
          min-height: 20px;
          animation: ${phase === 'fading' ? fadeOutKeyframes : fadeInKeyframes} 0.3s ease;
        `}
      >
        <span
          css={css`
            flex-shrink: 0;
            font-size: 10px;
            line-height: 1.4;
          `}
        >
          {currentBullet.emoji}
        </span>
        <span
          css={css`
            ${fontStyles.cat}
            color: ${colors.tungsten};
            display: inline;

            html[data-theme='dark'] & {
              color: ${colors.cloud};
            }
          `}
        >
          {displayText}
          {phase === 'typing' && (
            <span
              css={css`
                display: inline-block;
                width: 1px;
                height: 12px;
                background: ${colors.petrolBlue};
                margin-left: 1px;
                vertical-align: middle;
                animation: ${blinkKeyframes} 0.8s infinite;

                html[data-theme='dark'] & {
                  background: ${colors.skyBlue};
                }
              `}
            />
          )}
        </span>
      </div>

      {/* Progress dots */}
      <div
        css={css`
          display: flex;
          gap: ${spacing.micro};
          margin-top: ${spacing.micro};
        `}
      >
        {bullets.map((_, index) => (
          <div
            key={index}
            css={css`
              width: 4px;
              height: 4px;
              border-radius: 50%;
              background: ${index === currentIndex ? colors.petrolBlue : colors.cloud};
              transition: background 0.2s;

              html[data-theme='dark'] & {
                background: ${index === currentIndex ? colors.skyBlue : colors.tungsten};
              }
            `}
          />
        ))}
      </div>
    </div>
  );
};

export default TypewriterShowcase;
